// src/components/CalendarsDisplay.js
import {useState, useMemo} from "react";
import {Calendar, dateFnsLocalizer, Views} from "react-big-calendar";
import format from "date-fns/format";
import parse from "date-fns/parse";
import startOfWeek from "date-fns/startOfWeek";
import getDay from "date-fns/getDay";
//import enUS from "date-fns/locale/en-US"; // change si besoin
import fr from "date-fns/locale/fr"; // français si tu veux
import "react-big-calendar/lib/css/react-big-calendar.css";
import {useBookingDataContext} from "../context/BookingDataProvider";

import '../styles/CalendarsDisplay.css'

// Localisation
const locales = { fr };
const localizer = dateFnsLocalizer({ 
  format, 
  parse, 
  startOfWeek: () => startOfWeek(new Date(), {weekStartsOn: 1}), 
  getDay, 
  locales
});

// Times parser 
function parseISOWithTimezone(value) {
  // Exemple: 2025-11-29T08:00:00+01:00
  const d = new Date(value);
  if (!isNaN(d)) return d;

  // fallback : parser manuellement (rare)
  return new Date(Date.parse(value));
}

const COLORS = [
  "#4C78A8", // bleu doux
  "#F58518", // orange subtil
  "#54A24B", // vert équilibré
  "#E45756", // rouge framboise
  "#B279A2", // mauve pastel
  "#FF9DA7", // rose pâle
  "#9C755F", // brun doux
  "#BAB0AC", // gris chaud

  "#6BAED6", // bleu clair
  "#74C476", // vert clair
  "#E377C2", // rose/magenta doux
  "#8DA0CB", // bleu-lilas
  "#A6D854", // vert lime doux
  "#FFD92F", // jaune pastel
  "#E5C494", // sable chaud

  "#66C2A5", // vert turquoise
  "#FC8D62", // pêche
  "#8DA0CB", // bleu lavande
  "#E78AC3", // rose violine doux
  "#4b4353ff", 
  "#8c564b", 
  "#7f7f7f"
];

export default function CalendarsDisplay({ beginBooking })
{
  const {
    locations,
    events, 
    isLoading: loading, 
    error
  } = useBookingDataContext();
  
  // Calendar visible state
  const [visibleCalendars, setVisibleCalendars] = useState(
    Object.fromEntries(locations.map(loc => [loc.id, true]))
  );

  // 1. Local selection of locations (checkbox)
  const [visibleLocations, setVisibleLocations] = useState(() =>
    locations.filter(l => l.isSelected).map(l => l.calendar_id)
  );
  const toggleLocation = (calendarId) => {
    setVisibleLocations((old) =>
      old.includes(calendarId) 
        ? old.filter(x => x !== calendarId) 
        : [...old, calendarId]
    );
  };
  // 2. Assign colors deterministic by location id
  const calendarColors = useMemo(() => {
    const map = {};
    locations.forEach((loc, idx) => {
      if (!loc.calendar_id) return;
      map[loc.calendar_id] = COLORS[idx % COLORS.length];
    });
    return map;
  }, [locations]);

  // 3. Normalize events shape (ensure start/end are Date objects)
  const normalizedEvents = useMemo(() => {
    return (events || []).map(ev => {
      //const start = parseISOWithTimezone(ev.start);
      //const end = parseISOWithTimezone(ev.end);
      const start = new Date(ev.start);
      const end = new Date(ev.end); 
      
      const color = calendarColors[ev.calendarId] || "#888";
      const title = ev.title || ev.summary || "Sans titre";

      // Format lisible pour affichage et debug
      const endLabel = end.toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
      
      return {
        ...ev,
        start,
        end,
        title: ev.title || ev.summary || "Sans titre",  // `${ev.title} (jusqu’au ${endLabel})`,
        __color: calendarColors[ev.calendarId] || "#999"
      };
    });
  }, [events, calendarColors]);

  // 4. Filtering of events
  const filteredEvents = useMemo(() => {
    return normalizedEvents.filter(ev =>
      visibleLocations.includes(ev.calendarId)
    );
  }, [normalizedEvents, visibleLocations]);

  if (loading) return <p>Chargement des disponibilités...</p>;
  if (error) return <p className="error">Erreur: {error}</p>;
  if (!normalizedEvents.length) return <p>Aucun événement trouvé pour cette période.</p>;

  // Display a calendar pro location selected
  // const selectedLocations = locations.filter((loc) => loc.isSelected);

  // If singleCalendar true -> show one big calendar with all events,
  // otherwise show week view for convenience. You can render one Calendar per location if preferred.
  return (
    <div>
      {/* --- Locations checkboxes --- */}
      <div style={{ marginBottom: "1rem" }}>
        {locations.map((loc) => {
          const isChecked = visibleLocations.includes(loc.calendar_id);
          const color = calendarColors[loc.calendar_id] || "#666";

          return (

            <label key={loc.id} className="custom-checkbox-wrapper">
              {/* input hidden */}
              <input
                type="checkbox"
                className="custom-checkbox-input"
                checked={isChecked}
                onChange={() => toggleLocation(loc.calendar_id)}
              />
              {/* visible box */}
              <span
                className={`custom-checkbox-box ${isChecked ? "checked" : ""}`}
                style={{
                  backgroundColor: isChecked ? color : "transparent",
                  borderColor: color
                }}
              >
                <span className="custom-checkbox-check"></span>
              </span>

              {/* label */}
              <span style={{ color }}>{loc.title?.rendered || loc.name}</span>
            </label>
          );
        })}
      </div>

      {/* --- Calendar --- */}
      <div style={{ height: "650px" }}>
        <Calendar
          localizer={localizer}
          events={filteredEvents}
          startAccessor="start"
          endAccessor="end"
          titleAccessor="title"
          
          views={{
            month: true,
            week: true,
            day: true
          }}
          defaultView={Views.WEEK}  // "weeek"
          defaultDate={beginBooking ? new Date(beginBooking) : new Date()}

          // --- Times ranges ---
          min={new Date(2025, 1, 1, 8, 0)}  // 08:00
          max={new Date(2025, 1, 1, 23, 0)} // 23:00

          culture="fr"

          // Label français
          messages={{
            today: "Aujourd’hui",
            previous: "Précédent",
            next: "Suivant",
            month: "Mois",
            week: "Semaine",
            day: "Jour",
            year: "Année",
            agenda: "Agenda",
            showMore: (total) => `+${total} événement(s)`
          }}

          eventPropGetter={(event) => ({
            style: {
              backgroundColor: event.__color,
              borderColor: event.__color,
              // border: "none",
              color: "#fff",
              borderRadius: "4px",
              padding: "2px"  // padding: "2px 4px"
            },
          })}

          style={{ height: "100%", fontSize: "0.85rem" }} // style={{ height: "100%" }}
        />
      </div>
    </div>
  );
}